import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';
import { showsApi, type Show } from '../../api/shows';
import { formatDateIST, NotFoundImage } from '../../utils/function';
import { ActionButton } from '../common/ActionButton';
import { PageHeader } from '../common/PageHeader';
import { SearchAndFilter } from '../common/SearchAndFilter';
import ConfirmModal from '../model/ConfirmModal';
import Modal from '../model/Modal';
import { CrudActions } from '../ui/action-menu';
import { Badge } from '../ui/badge';
import { CustomPagination } from '../ui/CustomPagination';
import { ShowsTableSkeleton } from '../ui/skeleton';
import { cn } from '../ui/utils';
import ShowDetails from './ShowDetails';
import { ShowForm } from './ShowForm';
import { CategoryDisplay } from '../common/CategorySelector';

export default function ShowsManager() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>(
        (searchParams.get('status') as 'all' | 'active' | 'inactive') || 'all'
    );
    const page = Number(searchParams.get('page') ?? 1);
    const limit = Number(searchParams.get('limit') ?? 10);

    const [pagination, setPagination] = useState({
        total: 0,
        totalPages: 1
    });

    const [shows, setShows] = useState<Show[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentShow, setCurrentShow] = useState<Show | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showToDelete, setShowToDelete] = useState<string | null>(null);

    const handleItemsPerPageChange = (newLimit: number) => {
        updateSearchParams({
            limit: newLimit,
            page: 1
        });
        fetchShows(1, searchTerm, newLimit, statusFilter);
    };

    const fetchShows = async (page: number, search = '', limit: number, status?: 'all' | 'active' | 'inactive') => {
        try {
            setIsLoading(true);
            const response = await showsApi.getShows({
                page,
                limit,
                search,
                status: status === 'all' ? undefined : status as 'active' | 'inactive'
            });

            if (response.success) {
                setShows(response.data.data);
                setPagination({
                    total: response.data.pagination.total,
                    totalPages: response.data.pagination.totalPages
                });
            } else {
                setShows([]);
                setPagination({
                    total: 0,
                    totalPages: 1
                });
            }
        } catch (error) {
            console.error('Error fetching shows:', error);
            toast.error('Failed to fetch shows');
        } finally {
            setIsLoading(false);
        }
    };

    const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

    const updateSearchParams = (params: {
        search?: string;
        limit?: number;
        status?: string;
        page?: number;
    }) => {
        const newParams = new URLSearchParams(searchParams);

        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined || value === '' || value === 'all') {
                newParams.delete(key);
            } else {
                newParams.set(key, String(value));
            }
        });

        setSearchParams(newParams);
    };

    const handleStatusChange = (value: 'all' | 'active' | 'inactive') => {
        setStatusFilter(value);
        updateSearchParams({
            status: value,
            page: 1
        });
    };

    useEffect(() => {
        updateSearchParams({
            search: debouncedSearchTerm,
            status: statusFilter,
            page: 1,
            limit
        });
        fetchShows(1, debouncedSearchTerm, limit, statusFilter);
    }, [debouncedSearchTerm, limit, statusFilter]);

    const handleView = (show: Show) => {
        setCurrentShow(show);
    };

    const handleEdit = (show: Show) => {
        setCurrentShow(show);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (id: string) => {
        setShowToDelete(id);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!showToDelete) return;

        try {
            await showsApi.deleteShow(showToDelete);
            toast.success('Show deleted successfully');
            fetchShows(page, searchTerm, limit, statusFilter);
        } catch (error) {
            console.error('Error deleting show:', error);
            toast.error('Failed to delete show');
        } finally {
            setShowDeleteModal(false);
            setShowToDelete(null);
        }
    };

    const toggleStatus = async (id: string, currentStatus: boolean) => {
        try {
            const response = await showsApi.updateShowStatus(id, { isActive: !currentStatus });
            if (response.success) {
                if (statusFilter !== 'all') {
                    setIsLoading(false)
                    await fetchShows(page, searchTerm, limit, statusFilter);
                } else {
                    setShows(prevShows =>
                        prevShows.map(show =>
                            show.id === id ? { ...show, isActive: !currentStatus } : show
                        )
                    );
                }
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error('Error toggling show status:', error);
            toast.error('Failed to update show status');
        }
    };

    const resetForm = () => {
        setCurrentShow(null);
    };

    const handlePageChange = (newPage: number) => {
        if (newPage !== page) {
            updateSearchParams({ page: newPage, limit });
            fetchShows(newPage, searchTerm, limit, statusFilter);
        }
    };

    const handleAddShow = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const onSuccess = (newShow: Show) => {
        setIsModalOpen(false);
        resetForm();

        fetchShows(page, searchTerm, limit, statusFilter);
        void newShow;
        // if (!currentShow) {
        //     setShows(prev => [newShow, ...prev]);
        // } else {
        //     setShows(prev =>
        //         prev.map(show =>
        //             show.id === newShow.id ? newShow : show
        //         )
        //     );
        // }
    };

    const handleCloseViewModal = () => {
        setCurrentShow(null);
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Shows"
                description="Manage your radio shows."
                action={
                    <ActionButton
                        onClick={handleAddShow}
                        icon={<Plus className="w-4 h-4" />}
                    >
                        Add New Show
                    </ActionButton>
                }
            />

            <SearchAndFilter
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onClearSearch={() => setSearchTerm('')}
                statusFilter={statusFilter}
                onStatusChange={handleStatusChange}
                filterOptions={[
                    { value: "all", label: "All Shows" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                ]}
                searchPlaceholder="Search shows..."
            />

            <div className="bg-[#0B1623] border border-white/10 rounded-2xl">
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full">
                        <thead className="bg-white/5 border-b border-white/10">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Show</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Host</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Categories</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {isLoading ? (
                                <ShowsTableSkeleton />
                            ) : shows.length > 0 ? (
                                shows.map((show) => (
                                    <tr key={show.id} className="hover:bg-white/2 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div onClick={() => handleView(show)} className="flex cursor-pointer items-center space-x-4">
                                                <div className="h-10 w-10 shrink-0 rounded-md overflow-hidden">
                                                    <img
                                                        src={show.thumbnail || NotFoundImage}
                                                        alt={show.name}
                                                        className="h-full w-full object-cover"
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.src = NotFoundImage;
                                                        }}
                                                    />

                                                </div>
                                                <div>
                                                    <div className="font-medium text-white group-hover:text-cyan-300 transition-colors">
                                                        {show.name}
                                                    </div>
                                                    <div className="text-xs text-slate-400">
                                                        {formatDateIST(show.createdAt)}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-300">
                                            {show.hostName}
                                        </td>
                                        <td className="px-6 py-4">
                                            <CategoryDisplay categories={show.categories} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge
                                                variant="outline"
                                                className={cn(
                                                    "rounded-full text-xs font-medium cursor-pointer",
                                                    show.isActive
                                                        ? 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30'
                                                        : 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30'
                                                )}
                                                onClick={() => toggleStatus(show.id, show.isActive)}
                                            >
                                                {show.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <CrudActions
                                                onView={() => handleView(show)}
                                                onEdit={() => handleEdit(show)}
                                                onDelete={() => handleDeleteClick(show.id)}
                                            />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                                        {searchTerm || statusFilter !== 'all'
                                            ? 'No shows match your search criteria.'
                                            : 'No shows found. Get started by adding a new show.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {pagination.totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-white/10">
                            <CustomPagination
                                currentPage={page}
                                totalItems={pagination.total}
                                itemsPerPage={limit}
                                totalPages={pagination.totalPages}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                itemsPerPageOptions={[5, 10, 25, 50]}
                            />
                        </div>
                    )}
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    resetForm();
                }}
                title={currentShow ? 'Edit Show' : 'Add New Show'}
            >
                <ShowForm
                    initialData={currentShow}
                    onSuccess={onSuccess}
                    onCancel={() => {
                        setIsModalOpen(false);
                        resetForm();
                    }}
                />
            </Modal>


            <ShowDetails
                isOpen={!!currentShow && !isModalOpen}
                onClose={handleCloseViewModal}
                show={currentShow}
                onDelete={() => handleDeleteClick(currentShow?.id as string)}
                onEdit={()=> setIsModalOpen(true)}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Show"
                message="Are you sure you want to delete this show? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
            />
        </div>
    );
}